/**
 * CRM Client schemas.
 *
 * The CRM client (a lead/prospect/active customer) is the parent of
 * engagements. Compliance clients are a separate concept (see
 * `./compliance-client.ts`) — they mirror the CRM client via Strategy A
 * (engagement.jurisdiction wins).
 */

import { z } from 'zod';
import {
  idSchema, iso8601Schema, emailSchema, phoneE164Schema,
  urlSchema, pageInput, sortOrderSchema, booleanQuerySchema,
} from './common';
import { COUNTRY_CODES, JURISDICTION_CODES } from '../reference/jurisdictions';
import { USER_ROLES } from '../reference/roles';

export const clientStatusSchema = z.enum([
  'LEAD',
  'ACTIVE',
  'INACTIVE',
  'CHURNED',
]);

export const clientSourceSchema = z.enum([
  'REFERRAL',
  'WEBSITE',
  'LINKEDIN',
  'WHATSAPP',
  'COLD_OUTREACH',
  'EVENT',
  'CORPORATE',
]);

export const clientTagSchema = z.object({
  id: idSchema,
  name: z.string(),
  color: z.string(),
});
export type ClientTag = z.infer<typeof clientTagSchema>;

export const companyNumberSchema = z.string().trim().toUpperCase().regex(/^[A-Z0-9]{8}$/, 'Use the eight-character UK company number');

export const clientOutputSchema = z.object({
  id: idSchema,
  clientCode: z.string().regex(/^CLI-\d{5}$/),
  name: z.string(),
  company: z.string().nullable(),
  email: emailSchema.nullable(),
  phone: phoneE164Schema.nullable(),
  country: z.enum(COUNTRY_CODES as unknown as [string, ...string[]]).nullable(),
  industry: z.string().max(100).nullable(),
  website: urlSchema.nullable(),
  address: z.string().max(500).nullable(),
  source: clientSourceSchema.nullable(),
  status: clientStatusSchema,
  ownerId: idSchema.nullable(),
  createdById: idSchema,
  notesMarkdown: z.string().nullable(),
  // Legal identity (filled manually or via a review gate)
  companyNumber: companyNumberSchema.nullable().default(null),
  legalName: z.string().max(300).nullable(),
  tradeLicenseNo: z.string().max(100).nullable(),
  trn: z.string().max(50).nullable(),
  incorporationDate: iso8601Schema.nullable(),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).nullable(),
  fyEnd: iso8601Schema.nullable(),
  tags: z.array(clientTagSchema).default([]),
  retainerDecision: z.enum(['APPLIED', 'DEFERRED']).nullable().optional(),
  setupEngagementId: idSchema.nullable().optional(),
  setupAwaitingRetainer: z.boolean().optional(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type ClientOutput = z.infer<typeof clientOutputSchema>;

export const createClientInputSchema = z.object({
  name: z.string().min(1).max(200),
  company: z.string().max(200).nullable().optional(),
  email: emailSchema.nullable().optional(),
  phone: phoneE164Schema.nullable().optional(),
  country: z.enum(COUNTRY_CODES as unknown as [string, ...string[]]).nullable().optional(),
  industry: z.string().max(100).nullable().optional(),
  website: urlSchema.nullable().optional(),
  address: z.string().max(500).nullable().optional(),
  source: clientSourceSchema.nullable().optional(),
  status: clientStatusSchema.default('LEAD'),
  ownerId: idSchema.nullable().optional(),
  notesMarkdown: z.string().nullable().optional(),
  companyNumber: companyNumberSchema.nullable().optional(),
  legalName: z.string().max(300).nullable().optional(),
  tradeLicenseNo: z.string().max(100).nullable().optional(),
  trn: z.string().max(50).nullable().optional(),
  incorporationDate: iso8601Schema.nullable().optional(),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).nullable().optional(),
  fyEnd: iso8601Schema.nullable().optional(),
});
export type CreateClientInput = z.infer<typeof createClientInputSchema>;

export const updateClientInputSchema = createClientInputSchema.partial();
export type UpdateClientInput = z.infer<typeof updateClientInputSchema>;

export const listClientsInputSchema = pageInput.extend({
  status: clientStatusSchema.optional(),
  ownerId: idSchema.optional(),
  country: z.enum(COUNTRY_CODES as unknown as [string, ...string[]]).optional(),
  jurisdiction: z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]).optional(),
  /** Filter to clients carrying this tag (TagAssignment scope CLIENT). */
  tagId: idSchema.optional(),
  sortBy: z.enum(['createdAt', 'updatedAt', 'name', 'company', 'country', 'status']).default('updatedAt'),
  sortOrder: sortOrderSchema,
  /** When true, includes non-active statuses. Default false (ACTIVE only). */
  all: booleanQuerySchema.optional(),
});
export type ListClientsInput = z.infer<typeof listClientsInputSchema>;

/** Bulk delete — admin/GM only. */
export const bulkDeleteClientsInputSchema = z.object({
  ids: z.array(idSchema).min(1).max(500),
  password: z.string().min(8), // re-auth confirmation
});
export type BulkDeleteClientsInput = z.infer<typeof bulkDeleteClientsInputSchema>;

/** Bulk import clients. */
export const importClientsRowSchema = z.object({
  name: z.string().min(1).max(200),
  company: z.string().max(200).nullable().optional(),
  email: z.string().max(254).nullable().optional(),
  phone: z.string().max(20).nullable().optional(),
  country: z.string().max(10).nullable().optional(),
  industry: z.string().max(100).nullable().optional(),
  website: z.string().max(2048).nullable().optional(),
  address: z.string().max(500).nullable().optional(),
  source: z.string().max(50).nullable().optional(),
  status: z.string().max(50).nullable().optional(),
  notesMarkdown: z.string().nullable().optional(),
  ownerEmail: z.string().email().max(254).nullable().optional(),
});
export type ImportClientsRow = z.infer<typeof importClientsRowSchema>;

export const importClientsInputSchema = z.object({
  rows: z.array(importClientsRowSchema).min(1).max(500),
  dryRun: z.boolean().default(false),
});
export type ImportClientsInput = z.infer<typeof importClientsInputSchema>;

export const importClientsErrorSchema = z.object({
  row: z.number().int().min(0),
  name: z.string().optional(),
  field: z.string().optional(),
  message: z.string(),
});

export const importClientsOutputSchema = z.object({
  created: z.number().int().min(0),
  failed: z.number().int().min(0),
  errors: z.array(importClientsErrorSchema),
});
export type ImportClientsOutput = z.infer<typeof importClientsOutputSchema>;

/** Assign a client to an owner. */
export const assignClientInputSchema = z.object({
  ownerId: idSchema,
});
